import { Routes, Route } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import StatusPage from './pages/status/StatusPage';
import OpsPage from './pages/ops/OpsPage';
import LoginPage from './pages/LoginPage';
import AccessDeniedPage from './pages/AccessDeniedPage';
import AuthCallback from './pages/AuthCallback';
import { AuthProvider, useAuth } from './contexts/AuthContext';

/**
 * Protected route wrapper for Ops Dashboard.
 * Requires authentication and admin group membership.
 */
function ProtectedOpsRoute() {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  if (!isAdmin) {
    return <AccessDeniedPage />;
  }

  return <OpsPage />;
}

/**
 * Main App component with routing.
 *
 * Routes are determined by hostname:
 * - status.roboticks.io -> Status page (public)
 * - ops.roboticks.io -> Ops dashboard (requires admin auth)
 */
function AppRoutes() {
  const hostname = window.location.hostname;
  const isOpsPage = hostname.startsWith('ops.');

  // For development, allow /ops and /status paths
  if (import.meta.env.DEV) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/ops/*" element={<ProtectedOpsRoute />} />
        <Route path="/*" element={<StatusPage />} />
      </Routes>
    );
  }

  // Production: route based on hostname
  if (isOpsPage) {
    return (
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/*" element={<ProtectedOpsRoute />} />
      </Routes>
    );
  }

  return <StatusPage />;
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
